import StandardLogger from './logger/StandardLogger';
import {
  Appender,
  AppenderOptions,
  Logger,
  LoggerConfig,
  LoggerOptions
} from '../types/LoggerTypes';
import {
  ConsoleAppender,
  STD_CONSOLE_APPENDER_DEFAULT_OPTIONS,
  STD_CONSOLE_APPENDER_NAME
} from "./appenders/ConsoleAppender";
import {ApiAppender} from "./appenders/ApiAppender";
import NoOpLogger from "./logger/NoOpLogger";
import {LoggerProxy} from "./LoggerProxy";
import {AppenderType, LogLevel, SimpleLogger} from "@mosaic/logger-service";

export const ROOT_LOGGER_NAME: string = "ROOT";

export const ROOT_LOGGER_CONFIG: LoggerConfig = {
  logger: {
    level: LogLevel.INFO,
    component: ROOT_LOGGER_NAME,
    appendersRef: [STD_CONSOLE_APPENDER_NAME],
  },
  appenders: [STD_CONSOLE_APPENDER_DEFAULT_OPTIONS]
};

export default class LoggerFactory {
  private static loggers: Map<string, Logger> = new Map();
  private static appenders: Map<string, Appender> = new Map();

  private static noOpLogger: Logger = new NoOpLogger();

  static initializeSimple(config: LoggerConfig): SimpleLogger {
    this.initialize([config]);
    return new LoggerProxy(config.logger.component);
  }

  /**
   * Initialize the logger factory with given logger configurations.
   * It registers appenders and loggers then binds appenders to loggers.
   * @param configs array of LoggerConfig
   */
  static initialize(configs: LoggerConfig[]): void {
    // first all appenders
    configs.forEach(c => {
      (c.appenders ?? []).forEach(a => this.registerAppender(a));
    })
    // then all loggers
    configs.forEach(c => {
      this.registerLogger(c.logger);
    })
    // finally binding loggers/appenders
    configs.forEach(c => {
      const appenders = c.logger.appendersRef
        .map(n => this.getAppender(n))
        .filter((a): a is Appender => !!a);
      const logger = this.getLogger(c.logger.component);
      // bind appenders to logger
      if (logger && appenders.length > 0) {
        logger.bind(appenders);
      }
    })
  }

  /**
   * Returns the logger associated with the given component name. If no logger is found, it returns a NoOpLogger instance.
   * @param component
   */
  static getLogger(component: string): Logger {
    return this.loggers.get(component) ?? this.noOpLogger;
  }

  /**
   * Returns the appender associated with the given name. If no appender is found, it returns undefined.
   * @param name
   */
  static getAppender(name: string): Appender | undefined {
    return this.appenders.get(name) ?? undefined;
  }


  private static registerLogger(options: LoggerOptions): Logger {
    const key: string = options.component;

    let logger = this.loggers.get(key);
    if (!logger) {
      logger = new StandardLogger(options);
      this.loggers.set(key, logger);
    }

    return logger;
  }

  private static registerAppender(options: AppenderOptions): Appender | undefined {
    const key :string = options.name;

    if (!this.appenders.has(key)) {
      let instance: any;
      switch (options.type) {
        case AppenderType.CONSOLE:
          instance = new ConsoleAppender(options);
          break;
        case AppenderType.API:
          instance = new ApiAppender(options);
          break;
        default:
          console.warn(`Unknown appender ${options.name}. Register it!`);
          return undefined;
      }
      this.appenders.set(key, instance);
    }

    return this.appenders.get(key);
  }

  static clear(): void {
    this.loggers.clear();
    this.appenders.clear();
  }

  static clearLogger(name: string): void {
    this.loggers.delete(name);
  }
}
